import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import {
  MultiAirportDateRangeConfig,
  MultiAirportDateRangeFailure,
  MultiAirportDateRangePayloads,
} from "../models/airports/AirportConfig";
import { loadMultiAirportDateRanges } from "../services/multi-airport/MultiAirportDateRangeService";
import { AuthHelper } from "../utils/api/AuthHelper";

interface UseMultiAirportDateRangesResult {
  payload: MultiAirportDateRangePayloads;
  errors: MultiAirportDateRangeFailure[];
  isLoading: boolean;
  isLoaded: boolean;
}

const EMPTY_AIRPORTS: MultiAirportDateRangeConfig[] = [];
const EMPTY_PAYLOAD: MultiAirportDateRangePayloads = {
  otp: [],
  flights: [],
};

const getAirportCode = (airport: MultiAirportDateRangeConfig): string =>
  (airport.iataCode || airport.code || airport.id || "").trim();

export const useMultiAirportDateRanges = (
  airports: MultiAirportDateRangeConfig[] | undefined,
  currentTime: Date
): UseMultiAirportDateRangesResult => {
  const airportList = airports || EMPTY_AIRPORTS;
  const msalInstance = useMsal().instance as PublicClientApplication;
  const [payload, setPayload] =
    useState<MultiAirportDateRangePayloads>(EMPTY_PAYLOAD);
  const [errors, setErrors] = useState<MultiAirportDateRangeFailure[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let isCurrentRequest = true;

    if (airportList.length === 0) {
      setPayload(EMPTY_PAYLOAD);
      setErrors([]);
      setIsLoading(false);
      setIsLoaded(false);
      return () => {
        isCurrentRequest = false;
        controller.abort();
      };
    }

    const loadDateRanges = async () => {
      setIsLoading(true);
      setIsLoaded(false);

      try {
        const accessToken = await AuthHelper.getAccessToken(msalInstance);
        const result = await loadMultiAirportDateRanges(
          airportList,
          accessToken,
          currentTime,
          controller.signal
        );

        if (isCurrentRequest && !controller.signal.aborted) {
          setPayload(result.payload);
          setErrors(result.errors);
          setIsLoaded(true);
        }
      } catch (error) {
        if (isCurrentRequest && !controller.signal.aborted) {
          const message =
            error instanceof Error
              ? error.message
              : "Unable to obtain an access token";
          setPayload(EMPTY_PAYLOAD);
          setErrors(
            airportList.map((airport) => ({
              airportCode: getAirportCode(airport),
              target: "all",
              stage: "authentication",
              message,
            }))
          );
          setIsLoaded(true);
        }
      } finally {
        if (isCurrentRequest && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadDateRanges();

    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
  }, [airportList, currentTime, msalInstance]);

  return { payload, errors, isLoading, isLoaded };
};
